import React, { createContext, useContext, useEffect, useState, ReactNode } from "react";

export enum Theme {
  Light = "light",
  Dark = "dark",
}

interface ThemeContextType {
  theme: Theme;
  exchange: string;
  onSetTheme: (theme: Theme) => void;
  onSetExchange: (exchange: string) => void;
}

// Strips a trailing `-uat` so a client's UAT exchange name (e.g. `ANDX-UAT`)
// resolves to the same brand key as its prod counterpart (`andx`) — a new
// client's `-uat` variant then inherits its prod CSS overrides automatically,
// with no per-environment CSS duplication needed.
const normalizeExchangeAttr = (value: string | null): string => {
  const normalized = value
    ?.trim()
    .toLowerCase()
    .replace(/[^a-z0-9_-]/g, "")
    .replace(/-uat$/, "");
  return normalized || "default";
};

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

interface ThemeProviderProps {
  children: ReactNode;
  initialTheme?: Theme;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children, initialTheme = Theme.Light }) => {
  // Read initial theme from HTML element (set by inline script in index.html)
  const getInitialTheme = (): Theme => {
    const dataTheme = document.documentElement.getAttribute("data-theme");
    if (dataTheme === Theme.Dark) return Theme.Dark;
    if (dataTheme === Theme.Light) return Theme.Light;
    return initialTheme;
  };

  const [theme, setTheme] = useState<Theme>(getInitialTheme);
  const [exchange, setExchange] = useState<string>(normalizeExchangeAttr(document.documentElement.getAttribute("data-exchange")));

  const onSetTheme = (newTheme: Theme) => {
    setTheme(newTheme);
  };

  const onSetExchange = (newExchange: string) => {
    setExchange(normalizeExchangeAttr(newExchange));
  };

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
  }, [theme]);

  useEffect(() => {
    document.documentElement.setAttribute("data-exchange", exchange);
  }, [exchange]);

  return <ThemeContext.Provider value={{ theme, exchange, onSetTheme, onSetExchange }}>{children}</ThemeContext.Provider>;
};

export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);

  if (context === undefined) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }

  return context;
};
